import React, { FocusEventHandler, useCallback, useId } from 'react'
import { FocusScope } from 'react-aria'
import { useField } from 'react-final-form'
import { Box, Text } from '@chilipiper/design-system'
import {
  Select,
  Input,
  Radio,
  EndContent,
  TextArea,
  InputNumber,
  Checkbox,
  PillSelect,
  DatePicker,
  TimePicker,
  InputGroup,
} from '@chilipiper/design-system/src/new'
import { DataFieldType } from '@chilipiper/api-type-def'
import { t } from '@chilipiper/locale/src/new'
import { captureException } from '@chilipiper/sentry'
import { currentTimezoneDateTime } from '@chilipiper/date-time'
import { removeDotsFromDataFieldPath } from '../helpers'
import { useTranslations } from '../../../context/TranslationContext'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import { SubmittedFormField } from './types'
import { mapDatafieldTypeToAutoComplete } from './helpers'

interface FormFieldProps {
  dataField: SubmittedFormField
  isDisabled?: boolean
  submittedValue?: string | number | boolean
  autoFocus?: boolean
}

export const FormField = ({ dataField, isDisabled, submittedValue, autoFocus }: FormFieldProps) => {
  const initialValue = submittedValue ?? dataField.knownValue?.value
  const fieldProps = useField(removeDotsFromDataFieldPath(dataField.ref), {
    initialValue: initialValue === undefined ? undefined : String(initialValue),
  })
  const { meta, input } = fieldProps

  const onChange = useCallback(
    (value: string) => {
      if (dataField.dataTypeInfo.type === 'Email') {
        return input.onChange(value.trim().toLowerCase())
      }
      return input.onChange(value)
    },
    [dataField.dataTypeInfo.type, input]
  )

  const [required, defaultLabel] = useTranslations(['required', dataField.ref])
  const translatedDefaultLabel =
    defaultLabel === dataField.ref ? dataField.fieldLabel : defaultLabel
  const defaultRequiredError = t(required ?? '', {
    field: translatedDefaultLabel ?? '',
  })
  const { errorMessage, label } = (() => {
    return {
      errorMessage: dataField.translations.error ?? defaultRequiredError,
      label: dataField.translations.label ?? translatedDefaultLabel ?? 'No Label',
    }
  })()

  return (
    <Box data-test-id='FillFormBlock-field'>
      <FocusScope autoFocus={autoFocus}>
        <FormFieldContent
          onChange={onChange}
          onFocus={input.onFocus as FocusEventHandler}
          onBlur={input.onBlur as FocusEventHandler}
          isDisabled={isDisabled}
          errorMessage={meta.touched && meta.error ? errorMessage : undefined}
          dataFieldFieldLabel={dataField.fieldLabel}
          dataFieldType={dataField.dataTypeInfo.type}
          picklistValues={
            'values' in dataField.dataTypeInfo ? dataField.dataTypeInfo.values : undefined
          }
          label={label}
          value={input.value}
          autoComplete={mapDatafieldTypeToAutoComplete(dataField.ref)}
        />
      </FocusScope>
    </Box>
  )
}

export const FormFieldContent = ({
  value,
  dataFieldType,
  onChange,
  onFocus,
  onBlur,
  picklistValues,
  label,
  dataFieldFieldLabel,
  errorMessage,
  isDisabled,
  labelHidden,
  autoComplete,
}: {
  value: string
  dataFieldType: DataFieldType
  onChange: (value: string) => void
  onFocus?: (event: React.FocusEvent) => void
  onBlur?: (event: React.FocusEvent) => void
  picklistValues?: string[]
  label: string
  labelHidden?: boolean
  dataFieldFieldLabel: string
  errorMessage?: string
  isDisabled?: boolean
  autoComplete?: React.ComponentPropsWithoutRef<typeof Input>['autoComplete']
}) => {
  const id = useId()
  const { isNarrowScreen } = useMessageFeedContext()
  const size = isNarrowScreen ? 'large' : 'regular'
  return (() => {
    switch (dataFieldType) {
      case 'Picklist':
        return (
          <Select
            value={value}
            onChange={onChange}
            hasSearch={!!(picklistValues?.length ?? 0 > 3)}
            onFocus={onFocus}
            onBlur={onBlur}
            isDisabled={isDisabled}
            errorMessage={errorMessage}
            label={<Select.Label position={labelHidden ? 'hidden' : 'top'}>{label}</Select.Label>}
            data-test-id={`FillFormBlock-field-${dataFieldFieldLabel}`}
            size={size}
          >
            {picklistValues?.map(option => (
              <Select.Item
                key={option}
                value={option}
                data-test-id={`FillFormBlock-field-${dataFieldFieldLabel}-option-${option}`}
              >
                {option}
              </Select.Item>
            ))}
          </Select>
        )

      case 'RadioButton':
        return (
          <Radio.Group
            value={value}
            onChange={onChange}
            label={
              <Radio.GroupLabel
                position={labelHidden ? 'hidden' : 'top'}
                endContent={
                  errorMessage && (
                    <EndContent
                      variant='iconTooltip'
                      iconTooltipVariant='error'
                      content={errorMessage}
                    />
                  )
                }
              >
                {label}
              </Radio.GroupLabel>
            }
            name={id}
          >
            {picklistValues?.map(option => (
              <Radio.Item label={option} key={option} value={option} isDisabled={isDisabled} />
            ))}
          </Radio.Group>
        )

      case 'Checkbox':
        return (
          <Box>
            <Text display='block' textStyle='form-label' color='fg/major' mb={1}>
              {label}
            </Text>
            <Box overflow='hidden' boxShadow='border/section' borderRadius={8}>
              <PillSelect
                variant='regular'
                isExpandable={true}
                label={
                  <PillSelect.Label position='hidden'>
                    {label ?? `Select ${dataFieldType} value`}
                  </PillSelect.Label>
                }
                onChange={value => {
                  onChange(value.join(';'))
                }}
                values={value?.split(';').filter(value => value !== '') ?? []}
              >
                {picklistValues?.map(value => (
                  <PillSelect.Item key={value} value={value}>
                    {value}
                  </PillSelect.Item>
                ))}
              </PillSelect>
            </Box>
            {errorMessage && !value && (
              <Text
                display='block'
                textStyle='body-multiline-tight'
                data-test-id='input-error-message'
                color='fg/caution'
                mt={2}
              >
                {errorMessage}
              </Text>
            )}
          </Box>
        ) // This is not implemented yet and will change to MultiPicklist or something like that

      case 'TrueFalse':
        return (
          <Box>
            <Box overflow='hidden' boxShadow='border/section' borderRadius={8}>
              <Checkbox
                label={<Checkbox.Label>{label}</Checkbox.Label>}
                onChange={value => {
                  onChange(value ? 'true' : 'false')
                }}
                isChecked={value === 'true'}
              />
            </Box>
          </Box>
        )

      case 'LongText':
        return (
          <TextArea
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            isDisabled={isDisabled}
            placeholder='Enter...'
            errorMessage={errorMessage}
            label={
              <TextArea.Label position={labelHidden ? 'hidden' : 'top'}>{label}</TextArea.Label>
            }
            size={size}
          />
        )

      case 'Number':
        return (
          <InputNumber
            value={Number(value)}
            onChange={newValue => onChange(newValue?.toString() ?? '')}
            onFocus={onFocus}
            onBlur={onBlur}
            isDisabled={isDisabled}
            errorMessage={errorMessage}
            placeholder='Enter...'
            label={
              <InputNumber.Label position={labelHidden ? 'hidden' : 'top'}>
                {label}
              </InputNumber.Label>
            }
            size={size}
          />
        )

      case 'DateTime': {
        const { date, time, getUpdatedValue } = currentTimezoneDateTime(value)

        return (
          <InputGroup
            label={
              <InputGroup.Label position={labelHidden ? 'hidden' : 'top'}>{label}</InputGroup.Label>
            }
          >
            <InputGroup.Row gridAutoColumns='1fr 1fr'>
              <DatePicker
                label={<DatePicker.Label position='hidden'>{label}</DatePicker.Label>}
                value={date}
                onChange={newValue => {
                  onChange(getUpdatedValue({ date: newValue, time }))
                }}
                variant='nested'
                size={size}
              />
              <TimePicker
                label={<TimePicker.Label position='hidden'>{label}</TimePicker.Label>}
                value={time}
                onChange={newValue => {
                  onChange(getUpdatedValue({ date, time: newValue }))
                }}
                variant='nested'
                size={size}
              />
            </InputGroup.Row>
          </InputGroup>
        )
      }

      case 'Date': {
        return (
          <DatePicker
            label={
              <DatePicker.Label position={labelHidden ? 'hidden' : 'top'}>{label}</DatePicker.Label>
            }
            value={value}
            onChange={newValue => {
              if (newValue) {
                onChange(newValue)
              }
            }}
            size={size}
          />
        )
      }

      case 'Email':
      case 'Phone':
      case 'ShortText':
        return (
          <Input
            label={<Input.Label position={labelHidden ? 'hidden' : 'top'}>{label}</Input.Label>}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder='Enter...'
            errorMessage={errorMessage}
            isDisabled={isDisabled}
            autoComplete={autoComplete}
            data-test-id={`FillFormBlock-field-${dataFieldFieldLabel}`}
            size={size}
          />
        )

      default:
        dataFieldType satisfies never
        captureException(`Could not find data field component for field type: ${dataFieldType}`)
        return null
    }
  })()
}
